import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';

import {
    ActionsColumn,
    EnabledColumn,
    IdColumn,
    NameColumn,
    TypeColumn,
} from './columns';
import {
    Box,
    Button,
    Container,
    Flex,
    Group,
    MultiSelect,
    Portal,
    Select,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import { CreateChatHandlerModal, CreateMessageGroupModal } from './modals';
import { MRT_EditActionButtons, MantineReactTable } from 'mantine-react-table';
import { MultiSelectCreatable, StatColumn } from '../../common';
import {
    getChatHandlerById,
    getChatHandlers,
    getMessageGroups,
} from '../../../http/apiClient';
import {
    useDebouncedCallback,
    useDisclosure,
    useLocalStorage,
} from '@mantine/hooks';
import { useMemo, useState } from 'react';

import FiltersMultiSelect from '../../common/filtersMultiSelect/FiltersMultiSelect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MessageTable from './messageTable/MessageTable';
import { StatusColumn } from '../campaigns/campaignList/columns';
import classes from './GroupTable.module.css';
import { naiveRound } from '../../../utils/text';
import { sumObjectKeys } from '../../../utils/statUtils';
import { useEntityQueryUpdate } from '../../../hooks/useEntityQueryUpdate';
import { usePaginatedQuery } from '../../../http/queries/usePaginatedQuery';
import { useQueryClient } from '@tanstack/react-query';

const ChatHandlersView = ({ ...props }) => {
    const [globalFilter, setGlobalFilter] = useState(null);
    const [sorting, setSorting] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 20,
    });

    const filterDebounce = useDebouncedCallback((query) => {
        setGlobalFilter(query);
    }, 600);

    const [
        createModalOpened,
        { open: openCreateModal, close: closeCreateModal },
    ] = useDisclosure();

    const {
        isLoading,
        isError,
        error,
        data: paginatedData,
    } = usePaginatedQuery({
        queryFn: (queryParams) => getChatHandlers(queryParams),
        queryKey: ['chat_handlers'],
        refetchInterval: 15_0000,
        globalFilter,
        pagination,
        sorting,
    });

    const { data: messageGroups, pagination: dataPagination } =
        paginatedData || {};

    const columns = useMemo(
        () => [
            IdColumn(),
            NameColumn(),
            StatColumn('Sent', (x) => {
                const stats = x.scriptId ? x.scriptStats : x.botStats;
                return stats?.send;
            }),
            StatColumn('Clicks', (x) => {
                return sumObjectKeys(x.scriptId ? x.scriptStats : x.botStats, [
                    'uniqueClick',
                    'uniquePreview',
                ]);
            }),
            StatColumn(
                'CTR',
                (x) => {
                    const stats = x.scriptId ? x.scriptStats : x.botStats;
                    const uniqueClicks = sumObjectKeys(stats, [
                        'uniqueClick',
                        'uniquePreview',
                    ]);
                    return naiveRound((uniqueClicks / stats?.send) * 100, 2);
                },
                true
            ),
            StatColumn('Conversions', (x) => {
                const stats = x.scriptId ? x.scriptStats : x.botStats;
                return stats?.conversion;
            }),
            StatColumn(
                'CR',
                (x) => {
                    /*const uniqueClicks = sumObjectKeys(x.stats, [
                        'uniqueClick',
                        'uniquePreview',
                    ]);*/
                    const stats = x.scriptId ? x.scriptStats : x.botStats;
                    const uniqueClicks = stats?.send;
                    return naiveRound(
                        (stats?.conversion / uniqueClicks) * 100,
                        2
                    );
                },
                true
            ),
        ],
        [messageGroups]
    );

    /*const tableData = useMemo(
        () => (isLoading ? [] : campaigns),
        [campaigns, isLoading]
    );*/

    const tableData = useMemo(
        () => (isLoading ? [] : messageGroups),
        [messageGroups, isLoading]
    );

    const [columnVisibility, setColumnVisibility] = useLocalStorage({
        key: 'chat-handler-table-visibility', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const [columnSizing, setColumnSizing] = useLocalStorage({
        key: 'chat-handler-table-sizing', // a unique key for local storage
        defaultValue: {}, // default to all columns shown if empty
        getInitialValueInEffect: true,
    });

    const handleVisibilityChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnVisibility((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handlSizeChange = (changeFunc) => {
        const changes = changeFunc();
        setColumnSizing((prevState) => ({
            ...prevState,
            ...changes,
        }));
    };

    const handleFilterChange = (queryStr) => {
        filterDebounce(queryStr);
    };

    const entityUpdater = useEntityQueryUpdate();
    const handleQueryTest = () => {
        const newModel = {
            id: 4,
            userId: 'ce21af02-203c-4294-8791-247bd511a718',
            name: 'wutang',
        };
        entityUpdater(
            [['chat_handlers'], ['chat_handler', newModel.id]],
            newModel
        );
    };

    const tableProps = {
        columns: columns,
        data: tableData,
        enableRowActions: true,
        enableColumnResizing: true,
        enableColumnActions: false,
        enableSorting: false,
        enableTopToolbar: true,
        enableGlobalFilter: true,
        enableFilters: false,
        enableStickyHeader: true,
        enableDensityToggle: false,
        positionActionsColumn: 'last',
        layoutMode: 'semantic',
        mantineSearchTextInputProps: {
            placeholder: 'Search Chat Handlers',
            miw: 200,
        },
        mantineTableContainerProps: {
            style: {
                minHeight: '80vh',
            },
        },
        positionGlobalFilter: 'left',
        defaultColumn: {
            minSize: 0,
            maxSize: Number.MAX_SAFE_INTEGER,
        },
        renderTopToolbarCustomActions: ({ table }) => (
            <Group
                ms={'xs'}
                gap={'xs'}
                align='flex-start'
                w={'100%'}>
                <FiltersMultiSelect
                    options={[{ label: 'test', value: 'test' }]}
                />
                <Button onClick={openCreateModal}>Create Handler</Button>
            </Group>
        ),
        renderRowActions: ActionsColumn,

        manualPagination: true,
        manualSorting: true,
        createDisplayMode: 'modal', //default ('row', and 'custom' are also available)
        editDisplayMode: 'modal', //default ('row', 'cell', 'table', and 'custom' are also available)
        enableEditing: false,
        positionExpandColumn: 'last',
        onColumnVisibilityChange: handleVisibilityChange,
        onColumnSizingChange: handlSizeChange,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onGlobalFilterChange: handleFilterChange,
        rowCount: dataPagination?.totalItems || 0,
        pageCount: dataPagination?.totalPages || 1,
        manualExpanding: true,
        initialState: {
            columnVisibility,
            pagination,
            isLoading,
            showGlobalFilter: true,
        },
        state: {
            isLoading,
            pagination,
            columnVisibility,
            columnSizing,
            showAlertBanner: isError,
            showProgressBars: isLoading,
            sorting,
            density: 'xs',
        },
    };

    return (
        <Container fluid>
            <MantineReactTable {...tableProps} />
            <CreateChatHandlerModal
                opened={createModalOpened}
                close={closeCreateModal}
            />
        </Container>
    );
};
export default ChatHandlersView;
